import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/lib/toast';
import { queryKeys } from '@/lib/queryKeys';

export interface EbayTemplate {
	id: string;
	name: string;
	scope: 'preset' | 'user';
	description: string | null;
	body_html: string | null;
	wrapper_html: string | null;
	applies_to_game: string | null;
	applies_to_product_type: string | null;
	is_default: boolean;
	user_id: string | null;
}

const CURRENT_PRESET_NAME = 'CardSimp default';

function onlyCurrentPresets(templates: EbayTemplate[]) {
	return templates.filter((template) => {
		if (template.scope !== 'preset') return true;
		return template.name === CURRENT_PRESET_NAME;
	});
}

export function useEbayTemplates() {
	const { user } = useAuth();
	const qc = useQueryClient();

	const list = useQuery({
		queryKey: queryKeys.ebay.templates(user?.id),
		enabled: !!user?.id,
		refetchOnMount: 'always',
		select: onlyCurrentPresets,
		queryFn: async (): Promise<EbayTemplate[]> => {
			const { data, error } = await (supabase as any)
				.from('ebay_description_templates')
				.select('*')
				.or(`scope.eq.preset,user_id.eq.${user!.id}`)
				.order('scope', { ascending: false }) // user first, then preset
				.order('is_default', { ascending: false })
				.order('name', { ascending: true });
			if (error) throw error;
			return onlyCurrentPresets((data || []) as EbayTemplate[]);
		}
	});

	const save = useMutation({
		mutationFn: async (input: Partial<EbayTemplate> & { id?: string; name: string }) => {
			if (!user?.id) throw new Error('Not signed in');
			const row: any = {
				name: input.name,
				description: input.description ?? null,
				body_html: input.body_html ?? null,
				wrapper_html: input.wrapper_html ?? null,
				applies_to_game: input.applies_to_game ?? null,
				applies_to_product_type: input.applies_to_product_type ?? null,
				scope: 'user',
				user_id: user.id
			};
			if (input.id) {
				const { error } = await (supabase as any)
					.from('ebay_description_templates').update(row).eq('id', input.id);
				if (error) throw error;
				return input.id;
			}
			const { data, error } = await (supabase as any)
				.from('ebay_description_templates').insert(row).select('id').single();
			if (error) throw error;
			return data.id as string;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.ebay.templates(user?.id) });
			toast.success('Template saved');
		},
		onError: (e: any) => toast.error(`Could not save: ${e?.message || e}`)
	});

	const remove = useMutation({
		mutationFn: async (id: string) => {
			const { error } = await (supabase as any)
				.from('ebay_description_templates').delete().eq('id', id);
			if (error) throw error;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.ebay.templates(user?.id) });
			toast.success('Template deleted');
		},
		onError: (e: any) => toast.error(`Could not delete: ${e?.message || e}`)
	});

	return { list, save, remove };
}

export interface EbayCategoryResolution {
	categoryId: string;
	categoryName: string;
	suggestions: { categoryId: string; categoryName: string }[];
	source: 'static' | 'taxonomy';
}

export function useEbayCategoryResolution(itemId: string | undefined, enabled: boolean) {
	return useQuery({
		queryKey: queryKeys.ebay.category(itemId!),
		enabled: !!itemId && enabled,
		staleTime: 10 * 60 * 1000,
		queryFn: async (): Promise<EbayCategoryResolution> => {
			const { data, error } = await supabase.functions.invoke('ebay-taxonomy-resolve', {
				body: { item_id: itemId }
			});
			if (error) throw error;
			if ((data as any)?.error) throw new Error((data as any).error);
			return data as EbayCategoryResolution;
		}
	});
}
